import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ButtonGroup, Button, Form, InputGroup, Row, Col, Card, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { FaPlay, FaPause, FaRedo, FaVolumeUp, FaVolumeMute, FaExpand, FaCompress, FaHourglassHalf, FaStopwatch, FaBomb, FaStar } from 'react-icons/fa';

const Timer = () => {
  const [mode, setMode] = useState('countdown');
  
  const [inputHours, setInputHours] = useState('0');
  const [inputMinutes, setInputMinutes] = useState('0');
  const [inputSeconds, setInputSeconds] = useState('0');
  
  const [timeLeft, setTimeLeft] = useState(0);
  const [initialTime, setInitialTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  
  const [soundOn, setSoundOn] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isTimeLow, setIsTimeLow] = useState(false);

  const [accentColorKey, setAccentColorKey] = useState('puan1'); 

  // circleRef kaldırıldı
  const audioRef = useRef(null);
  const hoursInputRef = useRef(null);
  const minutesInputRef = useRef(null);
  const secondsInputRef = useRef(null);
  const timerContainerRef = useRef(null);

  const quickTimeOptions = useMemo(() => [
    { label: '1dk', h: 0, m: 1, s: 0 }, { label: '2dk', h: 0, m: 2, s: 0 },
    { label: '3dk', h: 0, m: 3, s: 0 }, { label: '5dk', h: 0, m: 5, s: 0 }, 
    { label: '10dk', h: 0, m: 10, s: 0 }, { label: '15dk', h: 0, m: 15, s: 0 }, 
    { label: '30dk', h: 0, m: 30, s: 0 }, { label: '1sa', h: 1, m: 0, s: 0 },
  ], []);

  const pointValueOptions = useMemo(() => [
    { key: 'puan1', label: '1', color: '#20c997', icon: <FaStar /> }, // Etiketler sadeleştirildi
    { key: 'puan2', label: '2', color: '#0dcaf0', icon: <FaStar /> },
    { key: 'puan3', label: '3', color: '#ffc107', icon: <FaStar /> },
    { key: 'puan4', label: '4', color: '#fd7e14', icon: <FaStar /> },
    { key: 'puan5', label: '5', color: '#dc3545', icon: <FaStar /> },
  ], []);
  
  const currentAccentColor = useMemo(() => {
    return pointValueOptions.find(p => p.key === accentColorKey)?.color || '#007bff';
  }, [accentColorKey, pointValueOptions]);

  const calculateTotalSeconds = useCallback((h, m, s) => (parseInt(h) || 0) * 3600 + (parseInt(m) || 0) * 60 + (parseInt(s) || 0), []);
  
  const formatTime = useCallback((totalSeconds) => {
    const absSeconds = Math.abs(totalSeconds);
    const h = Math.floor(absSeconds / 3600);
    const m = Math.floor((absSeconds % 3600) / 60);
    const s = absSeconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, []);

  // updateCircleAnimation fonksiyonu kaldırıldı.

  const handleSetTimeForCountdown = useCallback((h, m, s) => {
    const totalSecs = calculateTotalSeconds(h, m, s);
    setInputHours(String(parseInt(h) || 0));
    setInputMinutes(String(parseInt(m) || 0));
    setInputSeconds(String(parseInt(s) || 0));
    if (totalSecs > 0) {
      setTimeLeft(totalSecs); setInitialTime(totalSecs);
      setIsTimeLow(false);
    } else {
      setTimeLeft(0); setInitialTime(0);
      setIsTimeLow(false);
    }
    if (isActive) setIsActive(false);
  }, [calculateTotalSeconds, isActive]);

  const toggleTimer = useCallback(() => {
    if (mode === 'countdown') {
      if (timeLeft <= 0 && initialTime <= 0) {
        const currentTotalSeconds = calculateTotalSeconds(inputHours, inputMinutes, inputSeconds);
        if (currentTotalSeconds > 0) {
          handleSetTimeForCountdown(inputHours, inputMinutes, inputSeconds);
          setIsActive(true);
        } return;
      }
      if (initialTime > 0) setIsActive(!isActive);
    } else { setIsActive(!isActive); }
  }, [mode, timeLeft, initialTime, inputHours, inputMinutes, inputSeconds, isActive, calculateTotalSeconds, handleSetTimeForCountdown]);

  const resetTimer = useCallback(() => {
    setIsActive(false); setIsTimeLow(false);
    if (mode === 'countdown') {
      const h = parseInt(inputHours) || 0; const m = parseInt(inputMinutes) || 0; const s = parseInt(inputSeconds) || 0;
      handleSetTimeForCountdown(String(h), String(m), String(s));
    } else { setTimeLeft(0); }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [mode, inputHours, inputMinutes, inputSeconds, handleSetTimeForCountdown]);

  const quickSetCountdownTime = useCallback((h, m, s) => {
    if (mode === 'countdown') handleSetTimeForCountdown(String(h), String(m), String(s));
  }, [mode, handleSetTimeForCountdown]);
  
  useEffect(() => {
    let interval = null;
    if (isActive) {
      interval = setInterval(() => {
        if (mode === 'countdown') {
          setTimeLeft(prevTime => {
            const newTime = prevTime - 1;
            if (newTime < 0) {
              clearInterval(interval); setIsActive(false); setIsTimeLow(false);
              if (soundOn && audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current.play().catch(e => console.error("Ses çalma hatası:", e));
              }
              return 0;
            }
            const lowTimeThreshold = initialTime > 20 ? Math.max(1, Math.min(10, Math.floor(initialTime * 0.2))) : Math.max(1, Math.floor(initialTime * 0.3));
            if (newTime <= lowTimeThreshold && initialTime > lowTimeThreshold && !isTimeLow) setIsTimeLow(true);
            else if (newTime > lowTimeThreshold && isTimeLow) setIsTimeLow(false);
            // updateCircleAnimation kaldırıldı. Yeni görselin güncellenmesi progressPercent ile JSX'te yapılacak.
            return newTime;
          });
        } else { setTimeLeft(prevTime => prevTime + 1); }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, mode, soundOn, initialTime, isTimeLow]);

  const handleManualTimeSubmit = (e) => {
    e.preventDefault();
    if (mode === 'countdown') handleSetTimeForCountdown(inputHours, inputMinutes, inputSeconds);
  };

  const validateInput = (value, max) => {
    if (value === '') return '0'; const num = parseInt(value);
    if (isNaN(num) || num < 0) return '0';
    if (max !== null && num > max) return String(max);
    return String(num);
  };

  const toggleFullScreen = useCallback(() => {
    const elem = timerContainerRef.current; // Card elementine referans
    if (elem && !document.fullscreenElement) {
        elem.requestFullscreen().then(() => setIsFullScreen(true)).catch(err => console.error("Tam ekran hatası:", err));
    } else if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setIsFullScreen(false)).catch(err => console.error("Tam ekrandan çıkış hatası:", err));
    }
  }, []);

  useEffect(() => {
    const handleFsChange = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ([hoursInputRef.current, minutesInputRef.current, secondsInputRef.current].includes(document.activeElement)) return;
      if (e.key === ' ') { e.preventDefault(); toggleTimer(); }
      else if (e.key === 'Escape') { e.preventDefault(); isFullScreen ? toggleFullScreen() : resetTimer(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleTimer, resetTimer, isFullScreen, toggleFullScreen]);

  const progressPercent = mode === 'countdown' && initialTime > 0 ? (timeLeft / initialTime) * 100 : 0;
  const displayTime = formatTime(timeLeft);

  // Yeni çocuk dostu geri sayım görseli için segment sayısı
  const totalSegments = 10; // Örneğin 10 blok
  const activeSegments = mode === 'countdown' ? Math.ceil((progressPercent / 100) * totalSegments) : totalSegments;

  return (
    <div className="sifir-timer-app-wrapper"> 
      <div className="sifir-timer-peeking-cat">
        <span role="img" aria-label="cat paw">🐾</span>
        <span role="img" aria-label="cat paw" style={{transform: 'scaleX(-1) translateY(10px) translateX(-5px)'}}>🐾</span>
      </div>

      <Card ref={timerContainerRef} className="sifir-timer-card shadow-sm mx-auto">
        <Card.Body className="sifir-timer-card-body p-3 p-md-4">
          <Row>
            <Col lg={7} className={`sifir-timer-display-column text-center d-flex flex-column justify-content-center align-items-center mb-4 mb-lg-0`}>
              {mode === 'countdown' && (
                <div className={`sifir-timer-visual-countdown-container mb-4 ${isTimeLow ? 'sifir-timer-time-low-warning-visual' : ''}`}>
                  <div className="sifir-timer-segments-track">
                    {[...Array(totalSegments)].map((_, i) => (
                      <div 
                        key={i} 
                        className={`sifir-timer-segment ${i < activeSegments ? 'active' : ''}`} 
                        style={i < activeSegments ? {backgroundColor: isTimeLow ? '#dc3545' : currentAccentColor} : {}}
                      />
                    ))}
                  </div>
                  {isTimeLow && <FaBomb className="sifir-timer-bomb-icon-alt" />}
                </div>
              )}
              <div className={`sifir-timer-time-text ${mode === 'stopwatch' ? 'sifir-timer-stopwatch-time' : ''} ${isTimeLow && mode === 'countdown' ? 'sifir-timer-time-low-warning-text' : ''}`}>
                {displayTime}
              </div>
            </Col>

            <Col lg={5} className="sifir-timer-controls-column d-flex flex-column justify-content-center">
              {/* Toplam Puan Sistemi kaldırıldı */}
              <ToggleButtonGroup type="radio" name="mode" value={mode} onChange={(val) => { setMode(val); resetTimer(); }} className="sifir-timer-mode-toggle mb-3">
                <ToggleButton id="sifir-timer-tbg-countdown" value={'countdown'} className="sifir-timer-btn-mode"><FaHourglassHalf /> Geri Sayım</ToggleButton>
                <ToggleButton id="sifir-timer-tbg-stopwatch" value={'stopwatch'} className="sifir-timer-btn-mode"><FaStopwatch /> Kronometre</ToggleButton>
              </ToggleButtonGroup>

              {mode === 'countdown' && (
                <Form onSubmit={handleManualTimeSubmit} className="mb-3">
                  <Row className="g-2 mb-2">
                    <Col><InputGroup><Form.Control ref={hoursInputRef} type="number" value={inputHours} onChange={e => setInputHours(validateInput(e.target.value, 99))} disabled={isActive} placeholder="S" className="sifir-timer-input text-center"/><InputGroup.Text>SA</InputGroup.Text></InputGroup></Col>
                    <Col><InputGroup><Form.Control ref={minutesInputRef} type="number" value={inputMinutes} onChange={e => setInputMinutes(validateInput(e.target.value, 59))} disabled={isActive} placeholder="D" className="sifir-timer-input text-center"/><InputGroup.Text>DK</InputGroup.Text></InputGroup></Col>
                    <Col><InputGroup><Form.Control ref={secondsInputRef} type="number" value={inputSeconds} onChange={e => setInputSeconds(validateInput(e.target.value, 59))} disabled={isActive} placeholder="N" className="sifir-timer-input text-center"/><InputGroup.Text>SN</InputGroup.Text></InputGroup></Col>
                  </Row>
                  <Button type="submit" disabled={isActive} className="sifir-timer-btn sifir-timer-btn-set-time w-100">Süreyi Ayarla</Button>
                </Form>
              )}

              <ButtonGroup className="mb-3 d-flex">
                <Button onClick={toggleTimer} className={`sifir-timer-btn flex-grow-1 ${isActive ? 'sifir-timer-btn-pause' : 'sifir-timer-btn-start'}`}>{isActive ? <FaPause /> : <FaPlay />} {isActive ? 'Durdur' : 'Başlat'}</Button>
                <Button onClick={resetTimer} className="sifir-timer-btn sifir-timer-btn-reset flex-grow-1"><FaRedo /> Sıfırla</Button>
              </ButtonGroup>

              {mode === 'countdown' && (
                <div className="sifir-timer-quick-settings mb-3">
                  <h6 className="sifir-timer-section-title">Hızlı Ayarlar:</h6>
                  <ButtonGroup className="flex-wrap justify-content-center">
                    {quickTimeOptions.map((opt) => (
                      <Button key={opt.label} onClick={() => quickSetCountdownTime(opt.h, opt.m, opt.s)}
                        className="sifir-timer-btn sifir-timer-btn-quick-set m-1" disabled={isActive}>{opt.label}</Button>
                    ))}
                  </ButtonGroup>
                </div>
              )}
              
              <Row className="g-2">
                  <Col><Button onClick={() => setSoundOn(!soundOn)} className={`sifir-timer-btn sifir-timer-btn-utility w-100 ${soundOn ? 'sifir-timer-btn-sound-on' : 'sifir-timer-btn-sound-off'}`}>{soundOn ? <FaVolumeUp /> : <FaVolumeMute />}</Button></Col>
                  <Col><Button onClick={toggleFullScreen} className="sifir-timer-btn sifir-timer-btn-utility w-100">{isFullScreen ? <FaCompress /> : <FaExpand />}</Button></Col>
              </Row>

              <div className="sifir-timer-point-selector mt-3">
                <h6 className="sifir-timer-section-title">Vurgu Rengi / Tema:</h6>
                <ButtonGroup className="flex-wrap justify-content-center">
                  {pointValueOptions.map(p => (
                    <Button key={p.key} onClick={() => setAccentColorKey(p.key)}
                            className={`sifir-timer-btn-puan m-1 ${accentColorKey === p.key ? 'active' : ''}`}
                            style={accentColorKey !== p.key ? { backgroundColor: 'transparent', color: p.color, borderColor: p.color } : { backgroundColor: p.color, color: 'white', borderColor: p.color }}>
                      {p.icon} {p.label} {/* "PUAN" kelimesi kaldırıldı, sadece rakamlar kaldı */}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <audio 
        ref={audioRef} 
        src="/sesler/winfretless-39643.mp3" // Yeni ses dosyası yolu
        preload="auto" 
      />
    </div>
  );
};

export default Timer;